/**
 * Turn a failed AG-UI POST into a message a user can act on.
 *
 * The gate middlewares reject with a JSON `{ error, code, breakdown }` body — a
 * cost 402 (`COST_LIMIT_USER` / `_TENANT` / `_PAST_DUE` / …) or a concurrency
 * 429 (`AGENT_CONCURRENCY_CAP`) — and `error` is already a written-for-humans
 * sentence. We surface that verbatim; a bare `AG-UI 402` reads as an internal
 * fault and hides that the user just needs budget (or to wait for a run to
 * finish). The status fallback only fires when the body isn't the usual JSON
 * (proxy/network error), so it never overrides a real server message.
 *
 * ── The one case we DON'T take the server's word for ───────────────────
 *
 * Budget exhaustion (usage credits, C4). With overage dropped, running out is
 * a hard stop, and the copy has to name the action the READER can take: a
 * member can only escalate; an owner/admin can buy credits or wait for the
 * renewal date. The server picks that row too — it has to, for SDK/API callers
 * — but its message is unavoidably English (it renders with no access to the
 * locale framework; see the note in `shared/types/src/notifications.ts`).
 *
 * So for exactly those two codes we re-derive the SAME row through the shared
 * `selectExhaustionCopy` and render it from the i18n catalogue. One selector,
 * two renderers — hand-keeping a second copy of that four-way branch is the
 * drift that produced the two silent bucket-relabel bugs on `FundingBucket`.
 * Every other code still shows the server's sentence, unchanged.
 *
 * ── Why some of these carry a BUTTON and some deliberately do not ───────
 *
 * The sentence names the action the reader can take, and for two of the four
 * rows that action is a page in this app. Naming a purchase one click from the
 * page that sells it, and then not offering the click, is the worst moment in
 * the product — so `tenant_admin` and `tenant_admin_credits_off` get an action.
 *
 * `user_quota` and `tenant_member` get NONE, on purpose. Those readers can only
 * escalate to someone else; a button they cannot use is worse than a sentence,
 * because it reads as a route out and ends in a refusal.
 *
 * The branch is the SAME `selectExhaustionCopy` key that picked the sentence —
 * not a second condition. A second condition is precisely how a picker and a
 * gate drift into disagreeing about who may do what.
 *
 * The two admin rows share one destination (Settings → Billing) and differ only
 * in label, because their fixes differ: `tenant_admin` needs a PURCHASE, while
 * `tenant_admin_credits_off` has money already sitting behind a switch — the
 * selector only returns that row when the balance is non-zero. Sending the
 * second one to "buy credits" would sell someone something they already own.
 */
import i18n from '@papan/i18n'
import { selectExhaustionCopy } from '@papan/types'

/**
 * What the SPA should offer alongside a gate failure, or `null` for the rows
 * whose reader has nothing to click.
 *
 * `route` is deliberately abstract rather than a URL: this module is inside
 * `features/agent` and the destination is owned by the app shell, which is the
 * only place that knows the tenant prefix.
 */
export interface GateErrorAction {
  route: 'org-billing'
  label: string
}

export interface GateErrorResult {
  message: string
  action: GateErrorAction | null
}

interface GateErrorBody {
  error?: string
  message?: string
  code?: string
  breakdown?: {
    creditsEnabled?: boolean | null
    creditBalanceBillingMicro?: number | null
    poolRenewsAt?: string | null
    viewerCanManageBilling?: boolean
    /** Someone else's wallet funded this turn (0180). */
    payerIsElsewhere?: boolean
  }
}

/** The pool-renewal date in the viewer's locale. Undefined for a missing or
 *  unparseable timestamp so the caller drops the clause rather than printing
 *  "Invalid Date" — the sentence still works without it. */
function renewalDate(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return undefined
  try {
    return new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    }).format(at)
  } catch {
    return at.toISOString().slice(0, 10)
  }
}

/** Localized copy for a budget-exhaustion 402 plus what the reader may do about
 *  it, or undefined when this isn't one (dunning, disputes, app ceiling, grace
 *  gates — those keep the server's own message and offer nothing). */
function exhaustionResult(body: GateErrorBody): GateErrorResult | undefined {
  const b = body.breakdown ?? {}
  const key = selectExhaustionCopy({
    code: body.code,
    creditsEnabled: b.creditsEnabled,
    creditBalanceBillingMicro: b.creditBalanceBillingMicro,
    poolRenewsAt: b.poolRenewsAt,
    viewerCanManageBilling: b.viewerCanManageBilling,
    payerIsElsewhere: b.payerIsElsewhere,
  })
  switch (key) {
    // ── No action: these readers can only ask someone else ──────────────
    case 'user_quota':
      return { message: i18n.t('agent:budget.exhausted.userQuota'), action: null }
    // Someone ELSE's wallet funded this turn and is out (0180). Deliberately
    // actionless: every purchase route in this app tops up the tenant on
    // screen, which is not the one that was charged and cannot unblock the
    // turn — and this organization's own budget is untouched, so offering it
    // would be doubly wrong.
    case 'payer_elsewhere':
      return { message: i18n.t('agent:budget.exhausted.payerElsewhere'), action: null }
    case 'tenant_member':
      return { message: i18n.t('agent:budget.exhausted.tenantMember'), action: null }
    // ── Actionable: the fix is a page in this app ───────────────────────
    case 'tenant_admin_credits_off':
      return {
        message: i18n.t('agent:budget.exhausted.creditsOff'),
        // Not "buy" — the selector only returns this row when a NON-ZERO
        // balance is sitting behind the switch.
        action: { route: 'org-billing', label: i18n.t('agent:budget.exhausted.actionTurnOn') },
      }
    case 'tenant_admin': {
      const date = renewalDate(b.poolRenewsAt)
      return {
        message: date
          ? i18n.t('agent:budget.exhausted.tenantAdmin', { date })
          : i18n.t('agent:budget.exhausted.tenantAdminNoDate'),
        action: { route: 'org-billing', label: i18n.t('agent:budget.exhausted.actionBuy') },
      }
    }
    default:
      return undefined
  }
}

/**
 * The full result: a message, and an action when the reader has one.
 *
 * `aguiErrorMessage` below is the message-only form, kept because most callers
 * only ever wanted a string and threading an object through every one of them
 * would be churn for no gain.
 */
export async function aguiErrorResult(res: Response): Promise<GateErrorResult> {
  try {
    const body = (await res.json()) as GateErrorBody | null
    if (body) {
      const localized = exhaustionResult(body)
      if (localized) return localized
      // #1468 I1 review round 1 (m1): the caller's own chat shares its (pre-release) id with another project's chat
      // whose turn is running — a wait, not a permission fault
      if (body.code === 'SESSION_BUSY_ELSEWHERE') return { message: i18n.t('agent:session.busyElsewhere'), action: null }
      const msg = body.error || body.message
      if (msg) return { message: msg, action: null }
    }
  } catch {
    /* non-JSON body — fall through to the status-based default */
  }
  if (res.status === 402) return { message: i18n.t('agent:budget.exhausted.generic'), action: null }
  if (res.status === 429) {
    return { message: 'Too many AI tasks are running right now. Wait for one to finish, then try again.', action: null }
  }
  if (res.status === 401 || res.status === 403) {
    return { message: "You don't have permission to run the assistant here.", action: null }
  }
  return { message: `The assistant couldn't start (error ${res.status}).`, action: null }
}

export async function aguiErrorMessage(res: Response): Promise<string> {
  return (await aguiErrorResult(res)).message
}
